import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Loading, axiosInstance } from './api-configuration'
import {
  SettingsApi,
  PbAvailableReturnReasons,
  PbOptimizedCopyData,
  PbPortalSettings
} from '@itsrever/rever-api'
import { AxiosError } from 'axios'

export const settingsApi = new SettingsApi(undefined, undefined, axiosInstance)

interface SettingsCall {
  response: PbPortalSettings
  loading: Loading
  error?: AxiosError
}
interface CopiesCall {
  response: PbOptimizedCopyData
}

interface SettingsApiState {
  settings: SettingsCall
  copies: CopiesCall
}

const initialState: SettingsApiState = {
  settings: {
    response: {},
    loading: 'pending'
  },
  copies: {
    response: {}
  }
}

export const getSettings = createAsyncThunk(
  'settingsApi/getSettings',
  async (slug: string, { rejectWithValue }) => {
    try {
      const settings = await settingsApi.getECommerceSettings({ slug })
      return settings.data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const findItemReturnReasons = async (
  ecommerceId: string,
  categoryIds: Array<string>
) => {
  try {
    const response = await settingsApi.findItemReturnReasonsByBody({
      pbFindItemReturnReason: {
        ecommerce_id: ecommerceId,
        category_ids: categoryIds
      }
    })
    return response.data.return_reasons as Array<PbAvailableReturnReasons>
  } catch (error) {
    console.warn(error)
  }
}

const settingsApiSlice = createSlice({
  name: 'settingsApi',
  initialState,
  reducers: {
    setTheming: (state, action) => {
      if (action.payload && state.settings.response) {
        const newTheme = {
          ...state.settings.response.branding,
          ...action.payload
        }
        const newSettings = {
          ...state.settings,
          response: {
            ...state.settings.response,
            branding: newTheme
          }
        }
        state.settings = newSettings
      }
    },
    setCustomCopies: (state, action: PayloadAction<PbOptimizedCopyData>) => {
      state.copies.response = action.payload
    },
    setSettings: (state, action: PayloadAction<PbPortalSettings>) => {
      state.settings.response = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSettings.pending, (state) => {
        state.settings.loading = 'pending'
        state.settings.response = {}
      })
      .addCase(getSettings.fulfilled, (state, action) => {
        state.settings.loading = 'succeeded'
        state.settings.response = action.payload
      })
      .addCase(getSettings.rejected, (state, action) => {
        state.settings.loading = 'failed'
        state.settings.error = action.payload as AxiosError
      })
  }
})

export const { setCustomCopies, setSettings, setTheming } =
  settingsApiSlice.actions
export default settingsApiSlice.reducer
